import React, { useEffect } from 'react';
import { Form, Input, Select, Button, Radio } from 'antd';
import { FormInstance } from 'antd/es/form';
// Hooks
import { useGetAudienceFilters } from 'hooks';
// Types
import {
  CampaignLocationInput,
  CampaignTaskType,
  CampaignType,
  StoreGender,
  UserRole,
} from 'api/graphql-global-types';
import { SearchSocialShareCampaignStores_searchSocialShareCampaignStores_entities } from 'api/campaignV2/types/SearchSocialShareCampaignStores';
import { SearchSocialBoostCampaignStores_searchSocialBoostCampaignStores_entities } from 'api/campaignV2/types/SearchSocialBoostCampaignStores';
import { SearchCampaignStores_searchCampaignStores_entities } from 'api/campaignV2/types/SearchCampaignStores';
import { GetCampaignsV2_getCampaigns_entities } from 'api/campaignV2/types/GetCampaignsV2';
import { DeliverableV2, TimeLineDeliverable } from 'helpers/deliverables';
// Helpers
import {
  ATHLETIC_LEVEL,
  FOLLOWING_OPTIONS_MAX,
  FOLLOWING_OPTIONS_MIN,
  GENDER_OPTIONS,
  ALL_AGE_RANGES_VALUE,
  ALL_ETHNICITIES_VALUE,
  ALL_GENDERS_VALUE,
  ALL_INTERESTS_VALUE,
} from 'helpers/campaignEditModal';
import { ENGAGEMENT_RATE_OPTIONS, PLACE_PRIMARY_TYPES } from 'helpers/campaign';
// Components
import LocationSelect from '../../LocationSelect/LocationSelect';
import SportsSelect from '../../SportsSelect/SportsSelect';
import HashtagSelect from '../../HashtagSelect/HashtagSelect';
import TalentList from '../../../../pages/CampaignDetails/CampaignDetails/components/TalentList/TalentList';
import FormSection from '../FormSection/FormSection';
// Style
import styles from '../CampaignEditComponents.module.scss';

const { Option } = Select;

// Generic form values type that can handle different campaign types
type GenericFormValues = {
  userRole: UserRole[];
  budget?: number | null;
  budgetFrom?: number | null;
  budgetTo?: number | null;
  flatFeePerStore: number;
  genders?: StoreGender[] | null;
  maxAge?: number | null;
  minAge?: number | null;
  followingFrom: number | null;
  followingTo: number | null;
  sports?: string[] | null;
  locations?: CampaignLocationInput[] | null;
  excludedLocations?: CampaignLocationInput[] | null;
  // [key: string]: any; // Allow additional properties
};

type CampaignCriteriaProps = {
  form: FormInstance<any>;
  initialFormValues: GenericFormValues;
  record: GetCampaignsV2_getCampaigns_entities;
  isInstagram: boolean;
  setIsInstagram: (isInstagram: boolean) => void;
  isTwitter: boolean;
  setIsTwitter: (isTwitter: boolean) => void;
  isTikTok?: boolean;
  setIsTikTok?: (isTikTok: boolean) => void;
  setSelectedTasks: (tasks: CampaignTaskType[]) => void;
  deliverablesChecklist?: DeliverableV2[] | TimeLineDeliverable[];
  setDeliverablesChecklist?: (
    checklist: DeliverableV2[] | TimeLineDeliverable[]
  ) => void;
  handleFilterClick: () => void;
  loadingStoreData: boolean;
  talentsData?:
    | SearchCampaignStores_searchCampaignStores_entities[]
    | SearchSocialShareCampaignStores_searchSocialShareCampaignStores_entities[]
    | SearchSocialBoostCampaignStores_searchSocialBoostCampaignStores_entities[];
  hasMore: boolean;
  totalTalents: number;
  totalFollowers: number;
  showTalentList: boolean;
  showAthleticLevel?: boolean;
  onSelectTalent?: (talentId: string) => void;
  // Optional features for different campaign types
  showPlatformSelection?: boolean;
  showLocationSelection?: boolean;
  showHashtagSelection?: boolean;
  showEngagementRate?: boolean;
  // Book Me talent list pagination props
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize?: number) => void;
  showPagination?: boolean;
  selectedTalentId?: string | null;
  canEditPremiumFilters?: boolean;
  // Optional override for the "Price per Talent" field label/placeholder.
  // Used by Social Boost where this value is the GROSS per-talent budget,
  // not the athlete's net payout.
  flatFeePerStoreLabel?: string;
  // Social Boost sets the per-talent fee only at approval (pricePerShare),
  // so the edit modal hides the flat-fee input entirely.
  showFlatFeePerStore?: boolean;
};

const CampaignCriteria = ({
  record,
  form,
  initialFormValues,
  isInstagram,
  setIsInstagram,
  isTwitter,
  setIsTwitter,
  isTikTok = false,
  setIsTikTok,
  setSelectedTasks,
  setDeliverablesChecklist,
  handleFilterClick,
  loadingStoreData,
  talentsData,
  hasMore,
  totalTalents,
  totalFollowers,
  showTalentList,
  showAthleticLevel = false,
  // Book Me campaigns only, can be used as a flag to check if something is (not) needed for Book me campaigns
  onSelectTalent,
  showPlatformSelection = true,
  showLocationSelection = true,
  showHashtagSelection = true,
  showEngagementRate = true,
  currentPage = 1,
  pageSize = 8,
  showPagination = false,
  selectedTalentId,
  canEditPremiumFilters = false,
  flatFeePerStoreLabel = 'Price per Talent',
  showFlatFeePerStore = true,
}: CampaignCriteriaProps): JSX.Element => {
  const campaignType = record?.type ?? CampaignType.Social;
  const { data } = useGetAudienceFilters();
  const { ageRanges, ethnicities, genders, interests } =
    data?.getAudienceFilters || {};

  useEffect(() => {
    const currentLocations = form.getFieldValue('locations');
    if (
      currentLocations === undefined &&
      initialFormValues.locations !== undefined
    ) {
      form.setFieldsValue({ locations: initialFormValues.locations });
    } else if (currentLocations === undefined) {
      form.setFieldsValue({ locations: [] });
    }
  }, [form, initialFormValues.locations]);

  useEffect(() => {
    if (form.getFieldValue('excludedLocations') === undefined) {
      form.setFieldsValue({
        excludedLocations: initialFormValues.excludedLocations ?? [],
      });
    }
  }, [form, initialFormValues.excludedLocations]);

  const handleGenderChange = (value: string) => {
    if (value === ALL_GENDERS_VALUE) {
      // For genders, "All" means no selection (empty)
      form.setFieldsValue({ audienceGender: null });
    } else {
      form.setFieldsValue({ audienceGender: value });
    }
  };

  const handleAgeRangesChange = (value: string[]) => {
    if (value.includes(ALL_AGE_RANGES_VALUE)) {
      // Select all age ranges except the "All" option
      const allAgeRangeValues = ageRanges?.map((item) => item.value) || [];
      form.setFieldsValue({ audienceAgeRanges: allAgeRangeValues });
    } else {
      form.setFieldsValue({ audienceAgeRanges: value });
    }
  };

  const handleEthnicitiesChange = (value: string[]) => {
    if (value.includes(ALL_ETHNICITIES_VALUE)) {
      // Select all ethnicities except the "All" option
      const allEthnicityValues = ethnicities?.map((item) => item.value) || [];
      form.setFieldsValue({ audienceEthnicities: allEthnicityValues });
    } else {
      form.setFieldsValue({ audienceEthnicities: value });
    }
  };

  const handleInterestsChange = (value: string[]) => {
    if (value.includes(ALL_INTERESTS_VALUE)) {
      // Select all interests except the "All" option
      const allInterestValues = interests?.map((item) => item.value) || [];
      form.setFieldsValue({ audienceInterests: allInterestValues });
    } else {
      form.setFieldsValue({ audienceInterests: value });
    }
  };

  // Tiktok flag is used to display a different audience filter logic.
  // Tiktok campaigns are possible for social, social share and social boost.
  const isTikTokFilter =
    campaignType === CampaignType.Social ||
    campaignType === CampaignType.SocialShare ||
    campaignType === CampaignType.SocialBoost
      ? isTikTok ?? (!isInstagram && !isTwitter)
      : false;

  return (
    <>
      <FormSection
        title="Platform and talent"
        hint="Where the campaign runs and who is allowed to apply."
      >
        <div className={styles.rowWrapper}>
          {showPlatformSelection && (
            <div className={styles.field}>
              <span className={styles.fieldLabel} id="campaign-platform-label">
                Platform
              </span>

              <Radio.Group
                aria-labelledby="campaign-platform-label"
                value={
                  isInstagram ? 'instagram' : isTwitter ? 'twitter' : 'tiktok'
                }
                onChange={(e) => {
                  const platform = e.target.value;
                  setIsInstagram(platform === 'instagram');
                  setIsTwitter(platform === 'twitter');
                  setIsTikTok?.(platform === 'tiktok');

                  switch (platform) {
                    case 'instagram':
                      if (campaignType === CampaignType.SocialShare) {
                        setSelectedTasks([CampaignTaskType.IG_SHARE]);
                      } else {
                        setSelectedTasks([]);
                      }
                      setDeliverablesChecklist?.([]);
                      break;
                    case 'twitter':
                      if (campaignType === CampaignType.SocialShare) {
                        setSelectedTasks([CampaignTaskType.TW_SHARE]);
                      } else {
                        setSelectedTasks([]);
                      }
                      setDeliverablesChecklist?.([]);
                      break;
                    case 'tiktok':
                      setIsInstagram(false);
                      if (campaignType === CampaignType.SocialShare) {
                        setSelectedTasks([CampaignTaskType.TT_SHARE]);
                      } else {
                        setSelectedTasks([CampaignTaskType.TT_VIDEO]);
                      }
                      setDeliverablesChecklist?.([]);
                      form.setFieldsValue({
                        mediaType: [],
                        audienceEthnicities: [],
                        audienceInterests: [],
                      });
                      break;
                  }
                }}
              >
                <Radio.Button value="instagram">Instagram</Radio.Button>
                <Radio.Button value="tiktok">TikTok</Radio.Button>
              </Radio.Group>
            </div>
          )}

          <Form.Item name="userRole" label="Type of talent">
            <Select
              placeholder="Select athlete type"
              mode="multiple"
              allowClear
              options={[
                {
                  value: UserRole.Athlete,
                  label: 'Athlete',
                },
                {
                  value: UserRole.ContentCreator,
                  label: 'Content Creator',
                },
              ]}
            />
          </Form.Item>

          {showAthleticLevel && (
            <Form.Item name="athleticLevels" label="Athletic Level">
              <Select
                id="athleticLevels"
                placeholder="Select athletic level"
                mode="multiple"
                allowClear
                filterOption={(inputValue, option) =>
                  option?.props.children
                    .toString()
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
              >
                {ATHLETIC_LEVEL.map((item) => {
                  return (
                    <Option value={`${item.value}`} key={item.value}>
                      {item.label}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}
        </div>
      </FormSection>

      <FormSection
        title="Budget"
        hint={
          onSelectTalent
            ? 'The range the brand is willing to pay this talent.'
            : 'What the brand commits to the campaign, and what each talent is paid from it.'
        }
      >
        {onSelectTalent ? (
          <div className={styles.rowWrapper}>
            <Form.Item
              name="budgetFrom"
              rules={[
                {
                  required: true,
                  message: 'Minimum budget is required',
                },
                {
                  validator: (_, value) => {
                    if (value && value < 125) {
                      return Promise.reject(
                        new Error(
                          'Min budget must be greater than or equal to 125'
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              label="Min Budget"
            >
              <Input type="number" placeholder="Budget" aria-label="budget" />
            </Form.Item>
            <Form.Item
              name="budgetTo"
              rules={[
                {
                  required: true,
                  message: 'Maximum budget is required',
                },
              ]}
              label="Max Budget"
            >
              <Input type="number" placeholder="Budget" aria-label="budget" />
            </Form.Item>
          </div>
        ) : (
          <div className={styles.rowWrapper}>
            <Form.Item
              name="budget"
              rules={[
                {
                  required: true,
                  message: 'Budget is required',
                },
                {
                  validator: (_, value) => {
                    if (value && value < 2500) {
                      return Promise.reject(
                        new Error('Minimum campaign budget is $2500 USD.')
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              label="Budget"
            >
              <Input type="number" placeholder="Budget" aria-label="budget" />
            </Form.Item>
            {showFlatFeePerStore && (
              <Form.Item
                name="flatFeePerStore"
                rules={[
                  {
                    required: true,
                    message: `${flatFeePerStoreLabel} is required`,
                  },
                ]}
                label={flatFeePerStoreLabel}
              >
                <Input
                  type="number"
                  placeholder={flatFeePerStoreLabel}
                  aria-label={flatFeePerStoreLabel}
                />
              </Form.Item>
            )}
          </div>
        )}
      </FormSection>

      <FormSection
        title="Talent filters"
        hint="Who the campaign is offered to. Leave a filter empty to place no limit on it."
      >
        <div className={styles.rowWrapper}>
          <SportsSelect
            name="sports"
            label="Sport"
            placeholder="Select sport type"
            required={true}
            initialValue={initialFormValues.sports}
          />

          <Form.Item name="genders" label="Gender">
            <Select
              id="genders"
              placeholder="Select gender"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {GENDER_OPTIONS.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item name="minAge" label="Minimum Age">
            <Input
              type="number"
              placeholder="Minimum Age"
              aria-label="Minimum Age"
            />
          </Form.Item>

          <Form.Item name="maxAge" label="Maximum Age">
            <Input
              type="number"
              placeholder="Maximum Age"
              aria-label="Maximum Age"
            />
          </Form.Item>

          <Form.Item name="followingFrom" label="Social following min">
            <Select
              id="followingFrom"
              placeholder="No minimum"
              allowClear
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {FOLLOWING_OPTIONS_MIN.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.label}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item name="followingTo" label="Social following max">
            <Select
              id="followingTo"
              placeholder="No maximum"
              allowClear
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {FOLLOWING_OPTIONS_MAX.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.label}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          {showEngagementRate && (
            <Form.Item name="engagementRate" label="Engagement Rate">
              <Select
                placeholder="Select engagement rate"
                allowClear
                options={ENGAGEMENT_RATE_OPTIONS}
              />
            </Form.Item>
          )}

          {showHashtagSelection && (
            <Form.Item name="searchHashtags" label="Search Hashtags">
              <HashtagSelect
                name="searchHashtags"
                placeholder="Enter hashtags"
                value={form.getFieldValue('searchHashtags')}
                onChange={(hashtags) =>
                  form.setFieldsValue({ searchHashtags: hashtags })
                }
              />
            </Form.Item>
          )}

          {showLocationSelection && (
            <>
              <Form.Item
                name="locations"
                label="Location"
                extra="Where the talent is based now."
              >
                <LocationSelect
                  id="locations"
                  placeholder="Enter location"
                  onChange={(newLocations) => {
                    form.setFieldsValue({ locations: newLocations });
                  }}
                  value={form.getFieldValue('locations')}
                />
              </Form.Item>

              <Form.Item
                name="excludedLocations"
                label="Exclude Location"
                extra="Talent based here are left out."
              >
                <LocationSelect
                  id="excludedLocations"
                  placeholder="Enter excluded location"
                  onChange={(excludedLocations) => {
                    form.setFieldsValue({ excludedLocations });
                  }}
                  value={form.getFieldValue('excludedLocations')}
                />
              </Form.Item>

              <Form.Item
                name="hometownLocations"
                label="Hometown"
                extra="Where the talent is from."
              >
                <LocationSelect
                  id="hometownLocations"
                  placeholder="Enter hometown"
                  onChange={(hometownLocations) => {
                    form.setFieldsValue({ hometownLocations });
                  }}
                  value={form.getFieldValue('hometownLocations')}
                />
              </Form.Item>
            </>
          )}
        </div>
      </FormSection>

      <FormSection
        title="Audience filters"
        hint={
          canEditPremiumFilters
            ? 'Filters on the talent’s followers rather than the talent.'
            : 'Filters on the talent’s followers. The brand is on the free subscription, so these are read-only.'
        }
      >
        <div className={styles.rowWrapperThree}>
          <Form.Item name="audienceGender" label="Audience Gender">
            <Select
              id="audienceGender"
              placeholder="Select gender"
              onChange={handleGenderChange}
              disabled={!canEditPremiumFilters}
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              <Option value={ALL_GENDERS_VALUE} key={ALL_GENDERS_VALUE}>
                All Genders
              </Option>
              {genders?.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item name="audienceAgeRanges" label="Audience Age">
            <Select
              id="audienceAgeRanges"
              placeholder="Select age range"
              mode="multiple"
              allowClear
              onChange={handleAgeRangesChange}
              disabled={!canEditPremiumFilters}
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              <Option value={ALL_AGE_RANGES_VALUE} key={ALL_AGE_RANGES_VALUE}>
                All Age Ranges
              </Option>
              {ageRanges?.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="audienceEthnicities"
            label="Audience Ethnicity"
            extra={
              isTikTokFilter ? 'TikTok does not report ethnicity.' : undefined
            }
          >
            <Select
              id="audienceEthnicities"
              placeholder="Select ethnicity"
              mode="multiple"
              allowClear
              showSearch
              onChange={handleEthnicitiesChange}
              disabled={isTikTokFilter || !canEditPremiumFilters}
            >
              <Option value={ALL_ETHNICITIES_VALUE} key={ALL_ETHNICITIES_VALUE}>
                All Ethnicities
              </Option>
              {ethnicities?.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item name="audienceLocations" label="Audience Location">
            <LocationSelect
              id="audienceLocations"
              placeholder="Select location"
              onChange={(newLocations) => {
                form.setFieldsValue({ audienceLocations: newLocations });
              }}
              value={form.getFieldValue('audienceLocations') || []}
              disabled={!canEditPremiumFilters}
              saveFullState
              includedPrimaryTypes={
                isTikTokFilter
                  ? [PLACE_PRIMARY_TYPES.Country] // if TikTok, only show countries
                  : [
                      PLACE_PRIMARY_TYPES.Country,
                      PLACE_PRIMARY_TYPES.AdministrativeAreaLevel1,
                      PLACE_PRIMARY_TYPES.Locality,
                    ]
              }
            />
          </Form.Item>

          <Form.Item
            name="audienceInterests"
            label="Audience Interests"
            extra={
              isTikTokFilter ? 'TikTok does not report interests.' : undefined
            }
          >
            <Select
              id="audienceInterests"
              placeholder="Select interest"
              mode="multiple"
              allowClear
              showSearch
              onChange={handleInterestsChange}
              disabled={isTikTokFilter || !canEditPremiumFilters}
              filterOption={(inputValue, option) =>
                option?.label
                  ?.toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase()) ?? false
              }
            >
              <Option value={ALL_INTERESTS_VALUE} key={ALL_INTERESTS_VALUE}>
                All Interests
              </Option>
              {interests?.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </div>
      </FormSection>

      <FormSection
        title="Matching talent"
        hint="The talent that meets the filters above. Refresh the list after changing a filter."
      >
        <div className={styles.resultsHeader}>
          <div className={styles.filterButtonWrapper}>
            <Button
              type="primary"
              name="filterButton"
              loading={loadingStoreData}
              onClick={handleFilterClick}
            >
              Refresh List
            </Button>
          </div>
        </div>

        {showTalentList ? (
          <TalentList
            talents={talentsData || []}
            hasMore={hasMore}
            totalTalents={totalTalents}
            totalFollowers={totalFollowers}
            onSelectTalent={onSelectTalent}
            currentPage={currentPage}
            pageSize={pageSize}
            showPagination={showPagination}
            selectedTalentId={selectedTalentId}
          />
        ) : !loadingStoreData && talentsData !== undefined ? (
          <div className={styles.noItems}>
            No talent matches these filters. Widen a filter and refresh the
            list.
          </div>
        ) : null}
      </FormSection>
    </>
  );
};

export default CampaignCriteria;
